import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DropDownListItem } from '@shared/ui/organisms/drop-down-list/ReactSelectDropDownList';
import { fetchOfficeEntities, selectActiveOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { loadLockersForOffice, selectSelectedLockerOfficeId, setSelectedLockerOffice } from './lockers.duck';

const STORED_OFFICE_KEY = 'lockersSelectedOfficeId';

/**
 * The office picker sits on two pages and both need the same list, the same default and the same
 * "come back to where I was" behaviour, so it lives here rather than in either page.
 */
export function useLockerOffices(enabled: boolean) {
  const dispatch = useDispatch();
  const offices = useSelector(selectActiveOffices);
  const selectedOfficeId = useSelector(selectSelectedLockerOfficeId);

  const officeOptions: DropDownListItem[] = useMemo(() => {
    return offices.map((office: OfficeEntity) => ({ value: office.id, label: office.label }));
  }, [offices]);

  const selectedOfficeOption = useMemo(() => {
    return officeOptions.find((option: DropDownListItem) => option.value === selectedOfficeId);
  }, [officeOptions, selectedOfficeId]);

  const selectedOfficeName = selectedOfficeOption?.label ?? '';

  const selectOffice = useCallback((officeId: number) => {
    localStorage.setItem(STORED_OFFICE_KEY, `${officeId}`);
    dispatch(setSelectedLockerOffice(officeId));
  }, [dispatch]);

  // Nothing loads the office list on app start, so landing straight on a locker URL would other-
  // wise leave a member with no office and no dropdown to fix it with.
  useEffect(() => {
    if (!enabled || offices.length > 0) return;
    dispatch(fetchOfficeEntities());
  }, [dispatch, enabled, offices.length]);

  // A remembered office that has since been deleted or deactivated falls back to the first one.
  useEffect(() => {
    if (!enabled || selectedOfficeId || offices.length === 0) return;

    const stored = Number(localStorage.getItem(STORED_OFFICE_KEY));
    const remembered = offices.find((office: OfficeEntity) => office.id === stored);
    dispatch(setSelectedLockerOffice(remembered ? remembered.id : offices[0].id));
  }, [dispatch, enabled, selectedOfficeId, offices]);

  useEffect(() => {
    if (!enabled || !selectedOfficeId) return;
    dispatch(loadLockersForOffice(selectedOfficeId));
  }, [dispatch, enabled, selectedOfficeId]);

  return { officeOptions, selectedOfficeOption, selectedOfficeId, selectedOfficeName, selectOffice };
}
